import React, { useState, useMemo, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { transitions, durations, easings, reducedMotionCSS } from '../../utils/motion';

/* ── Types ── */

export interface Column<T> {
  header: string;
  accessor: keyof T;
  render?: (value: any, row: T) => ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface PaginationConfig {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor?: (row: T, index: number) => string;
  isLoading?: boolean;
  error?: string;
  emptyMessage?: string;
  emptyAction?: ReactNode;
  onRowClick?: (row: T) => void;
  pagination?: PaginationConfig;
  sortable?: boolean;
  className?: string;
  /**
   * Densidade vertical das celulas. O DESIGN.md prescreve "Compact (8px) ou
   * Comfortable (12px)" para tabelas densas; antes o padding era fixo em 12px.
   */
  density?: 'compact' | 'comfortable';
}

/* ── Skeleton Row ── */

function SkeletonRow({ columns }: { columns: number }): JSX.Element {
  return (
    <tr>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} style={{ padding: '0.75rem 1rem' }}>
          <div
            className="ds-skeleton"
            style={{
              height: '16px',
              width: `${60 + Math.random() * 30}%`,
              borderRadius: '4px',
              background: 'linear-gradient(90deg, #eceef0 25%, #c4c6cf 50%, #eceef0 75%)',
              backgroundSize: '200% 100%',
              animation: `ds-skeleton-shimmer ${durations.shimmer} ${easings.inOut} infinite`,
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/* ── Table Component ── */

export function Table<T extends object>({
  columns,
  data,
  keyExtractor,
  isLoading = false,
  error,
  emptyMessage = 'Nenhum registro encontrado',
  emptyAction,
  onRowClick,
  pagination,
  sortable = false,
  density = 'comfortable',
  className,
}: TableProps<T>): JSX.Element {
  const [sortField, setSortField] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (accessor: keyof T): void => {
    if (!sortable) return;

    if (sortField === accessor) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(accessor);
      setSortDirection('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortField || !sortable) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const strA = String(aVal);
      const strB = String(bVal);

      return sortDirection === 'asc'
        ? strA.localeCompare(strB, 'pt-BR')
        : strB.localeCompare(strA, 'pt-BR');
    });
  }, [data, sortField, sortDirection, sortable]);

  // ── Padroes do DESIGN.md para Dense Data Tables ──
  // header sticky, zebra acima de 10 colunas, numeros a direita em mono.
  const cellPaddingY = density === 'compact' ? '0.5rem' : '0.75rem';
  const cellPadding = `${cellPaddingY} 1rem`;
  const useZebra = columns.length > 10;
  const monoFor = (align?: string): string | undefined =>
    align === 'right' ? "'JetBrains Mono', ui-monospace, monospace" : undefined;

  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize)
    : 1;

  const getKey = (row: T, index: number): string => {
    if (keyExtractor) return keyExtractor(row, index);
    return String(index);
  };

  /* ── Error state ── */
  if (error) {
    return (
      <div className={cn('ds-table__error', className)} role="alert" style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#ba1a1a', fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar dados</p>
        <p style={{ color: '#888', fontSize: '0.875rem', margin: 0 }}>{error}</p>
      </div>
    );
  }

  /* ── Loading state (skeleton rows) ── */
  if (isLoading) {
    return (
      <div className={cn('ds-table__loading', className)} style={{ width: '100%', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ds-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={String(col.accessor)} style={{ padding: '0.75rem 1rem', borderBottom: '2px solid #c4c6cf' }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} columns={columns.length} />
              ))}
            </tbody>
          </table>
        </div>
        <style>{`${reducedMotionCSS('.ds-skeleton', '.ds-table__row', '.ds-table__page-btn')}`}</style>
      </div>
    );
  }

  /* ── Empty state ── */
  if (!isLoading && data.length === 0) {
    return (
      <div
        className={cn('ds-table__empty', className)}
        style={{
          padding: '3rem 2rem',
          textAlign: 'center',
          color: '#74777f',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <p style={{ margin: 0, fontSize: '0.875rem' }}>{emptyMessage}</p>
        {emptyAction}
      </div>
    );
  }

  /* ── Data state ── */
  return (
    <div className={cn('ds-table', className)} style={{ width: '100%', overflow: 'hidden' }}>
      <div className="ds-table__scroll" style={{ overflowX: 'auto' }}>
        <table
          className="ds-table__grid"
          role="grid"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
          }}
        >
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.accessor)}
                  scope="col"
                  style={{
                    textAlign: col.align ?? 'left',
                    padding: cellPadding,
                    background: '#f2f4f6',
                    color: '#43474e',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '2px solid #c4c6cf',
                    whiteSpace: 'nowrap',
                    width: col.width,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    cursor: sortable && col.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                  onClick={() => {
                    if (col.sortable) {
                      handleSort(col.accessor);
                    }
                  }}
                  aria-sort={
                    sortField === col.accessor
                      ? sortDirection === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    {col.header}
                    {sortable && col.sortable && sortField === col.accessor && (
                      <span style={{ fontSize: '0.65rem', color: '#002045' }}>
                        {sortDirection === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <tr
                key={getKey(row, rowIndex)}
                onClick={() => onRowClick?.(row)}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onRowClick(row);
                  }
                }}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: `background ${transitions.fast}`,
                }}
                className="ds-table__row"
              >
                {columns.map((col) => (
                  <td
                    key={String(col.accessor)}
                    style={{
                      textAlign: col.align ?? 'left',
                      padding: cellPadding,
                      borderBottom: '1px solid #eceef0',
                      color: '#191c1e',
                      whiteSpace: 'nowrap',
                      fontFamily: monoFor(col.align),
                      fontVariantNumeric: col.align === 'right' ? 'tabular-nums' : undefined,
                      background:
                        useZebra && rowIndex % 2 === 1 ? '#f7f9fb' : undefined,
                    }}
                  >
                    {col.render
                      ? col.render(row[col.accessor], row)
                      : (row[col.accessor] as ReactNode) ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {pagination && totalPages > 1 && (
        <div
          className="ds-table__pagination"
          role="navigation"
          aria-label="Navegacao de paginas"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            padding: '1rem 0',
          }}
        >
          <button
            className="ds-table__page-btn"
            disabled={pagination.page === 0}
            onClick={() => pagination.onPageChange(0)}
            aria-label="Primeira pagina"
            style={pageBtnStyle}
          >
            &laquo;
          </button>
          <button
            className="ds-table__page-btn"
            disabled={pagination.page === 0}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
            aria-label="Pagina anterior"
            style={pageBtnStyle}
          >
            &lsaquo;
          </button>

          <span style={{ fontSize: '0.8125rem', color: '#666', padding: '0 0.5rem' }}>
            {pagination.page * pagination.pageSize + 1}-
            {Math.min((pagination.page + 1) * pagination.pageSize, pagination.total)}{' '}
            de {pagination.total}
          </span>

          <button
            className="ds-table__page-btn"
            disabled={pagination.page >= totalPages - 1}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
            aria-label="Proxima pagina"
            style={pageBtnStyle}
          >
            &rsaquo;
          </button>
          <button
            className="ds-table__page-btn"
            disabled={pagination.page >= totalPages - 1}
            onClick={() => pagination.onPageChange(totalPages - 1)}
            aria-label="Ultima pagina"
            style={pageBtnStyle}
          >
            &raquo;
          </button>
        </div>
      )}

      <style>{`
        .ds-table__row:hover td {
          background: #eceef0;
        }
        .ds-table__row:focus-visible {
          outline: 2px solid #002045;
          outline-offset: -2px;
        }
        .ds-table__page-btn:hover:not(:disabled) {
          background: #f2f4f6;
        }
        ${reducedMotionCSS('.ds-table__row', '.ds-table__page-btn')}
      `}</style>
    </div>
  );
}

const pageBtnStyle: React.CSSProperties = {
  padding: '0.375rem 0.75rem',
  border: '1px solid #c4c6cf',
  background: '#ffffff',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: '#191c1e',
  fontFamily: 'inherit',
  transition: `background ${transitions.fast}`,
};
